/*
 * "Out There With Oricom" UGC video coverflow (see sections/feed-slider.liquid).
 * Ported from the Kaon reference. An INFINITE scroll-snap peek carousel: the item
 * nearest the track centre is marked .is-active (CSS grows + brightens it and hides
 * its play button) and its video autoplays muted; the rest are dimmed and paused.
 *
 * Infinite loop: the item set is cloned COPIES times on each side. Emphasis updates
 * live while scrolling (landing on a clone is fine — it's identical). We recentre
 * (teleport scrollLeft from a clone to its real twin) only once scrolling comes to
 * REST, with transitions suppressed, so the jump is invisible.
 */
if (!customElements.get('feed-slider')) {
  const COPIES = 1; // clone sets on each side (runway for fast flicks)
  const IDLE_MS = 140; // "scrolling has stopped" debounce

  class OutInTheWild extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.feed__track');
      if (!this.track) return;

      const originals = Array.from(this.track.querySelectorAll(':scope > .feed__item'));
      this.realCount = originals.length;
      if (this.realCount === 0) return;

      this.loop = this.realCount > 2;
      if (this.loop) {
        const before = document.createDocumentFragment();
        const after = document.createDocumentFragment();
        for (let c = 0; c < COPIES; c += 1) {
          originals.forEach((node) => before.appendChild(this.cloneItem(node)));
          originals.forEach((node) => after.appendChild(this.cloneItem(node)));
        }
        this.track.insertBefore(before, this.track.firstChild);
        this.track.appendChild(after);
      }

      this.items = Array.from(this.track.querySelectorAll(':scope > .feed__item'));
      this.realStart = this.loop ? COPIES * this.realCount : 0;
      this.activeIndex = -1;
      this.soundOn = false;

      this.items.forEach((item, i) => {
        item.addEventListener('click', (evt) => {
          if (i !== this.activeIndex && !evt.target.closest('a')) {
            evt.preventDefault();
            this.goTo(i);
          }
        });
      });

      const prevBtn = this.querySelector('.feed__arrow--prev');
      const nextBtn = this.querySelector('.feed__arrow--next');
      if (prevBtn) prevBtn.addEventListener('click', () => this.goTo(this.activeIndex - 1));
      if (nextBtn) nextBtn.addEventListener('click', () => this.goTo(this.activeIndex + 1));

      this.track.addEventListener('click', (evt) => {
        const btn = evt.target.closest('.feed__sound');
        if (!btn) return;
        evt.preventDefault();
        evt.stopPropagation();
        this.toggleSound();
      });

      this.onScroll = () => {
        if (!this.raf) {
          this.raf = requestAnimationFrame(() => {
            this.raf = null;
            this.updateActive();
          });
        }
        clearTimeout(this.idleTimer);
        this.idleTimer = setTimeout(() => this.recenter(), IDLE_MS);
      };
      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      let attempts = 0;
      const centre = () => {
        if (getComputedStyle(this.track).display === 'flex' || attempts > 30) {
          this.goTo(this.realStart + Math.floor(this.realCount / 2), 'auto');
          this.updateActive();
        } else {
          attempts += 1;
          requestAnimationFrame(centre);
        }
      };
      requestAnimationFrame(centre);
    }

    cloneItem(node) {
      const clone = node.cloneNode(true);
      clone.dataset.clone = 'true';
      clone.classList.remove('is-active');
      clone.querySelectorAll('video').forEach((v) => {
        v.removeAttribute('autoplay');
        v.muted = true;
      });
      return clone;
    }

    disconnectedCallback() {
      if (this.onScroll) this.track.removeEventListener('scroll', this.onScroll);
      clearTimeout(this.idleTimer);
    }

    centreIndex() {
      const trackRect = this.track.getBoundingClientRect();
      const trackCentre = trackRect.left + trackRect.width / 2;
      let best = 0;
      let bestDist = Infinity;
      this.items.forEach((item, i) => {
        const r = item.getBoundingClientRect();
        const dist = Math.abs(r.left + r.width / 2 - trackCentre);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      });
      return best;
    }

    updateActive() {
      const best = this.centreIndex();
      if (best !== this.activeIndex) this.setActive(best);
      else this.syncVideos();
    }

    recenter() {
      if (!this.loop) return;
      const best = this.centreIndex();
      const rel = best - this.realStart;
      const mod = ((rel % this.realCount) + this.realCount) % this.realCount;
      const target = this.realStart + mod;
      if (target === best) {
        this.syncVideos();
        return;
      }
      this.track.classList.add('is-jumping');
      this.jump(this.items[target].offsetLeft - this.items[best].offsetLeft);
      this.setActive(target);
      void this.track.offsetWidth;
      requestAnimationFrame(() => this.track.classList.remove('is-jumping'));
    }

    jump(delta) {
      const prev = this.track.style.scrollBehavior;
      this.track.style.scrollBehavior = 'auto';
      this.track.scrollLeft += delta;
      this.track.style.scrollBehavior = prev || '';
    }

    setActive(index) {
      this.activeIndex = index;
      this.items.forEach((item, i) => item.classList.toggle('is-active', i === index));
      this.syncVideos();
    }

    syncVideos() {
      this.items.forEach((item, i) => {
        const video = item.querySelector('video');
        if (!video) return;
        if (i === this.activeIndex) {
          video.muted = !this.soundOn;
          if (!video.dataset.revealBound) {
            video.dataset.revealBound = '1';
            const reveal = () => {
              if (this.items[this.activeIndex] === item) item.classList.add('is-playing');
            };
            video.addEventListener('playing', reveal);
            video.addEventListener('timeupdate', reveal);
          }
          const played = video.play();
          if (played && typeof played.catch === 'function') played.catch(() => {});
          if (video.readyState >= 3 && !video.paused) item.classList.add('is-playing');
        } else {
          if (!video.muted) video.muted = true;
          if (!video.paused) video.pause();
          item.classList.remove('is-playing');
        }
      });
    }

    toggleSound() {
      this.soundOn = !this.soundOn;
      this.classList.toggle('is-sound-on', this.soundOn);
      this.querySelectorAll('.feed__sound').forEach((b) => {
        b.setAttribute('aria-pressed', String(this.soundOn));
      });
      this.syncVideos();
    }

    goTo(index, behavior = 'smooth') {
      const item = this.items[index];
      if (!item) return;
      const trackRect = this.track.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      const delta = itemRect.left + itemRect.width / 2 - (trackRect.left + trackRect.width / 2);
      this.track.scrollTo({ left: this.track.scrollLeft + delta, behavior });
    }
  }

  customElements.define('feed-slider', OutInTheWild);
}
